import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Sentry } from '../lib/sentry';

interface MarkdownDocProps {
  docPath: string; // e.g., '/policy/TOS.md' or '/policy/PRIVACY.md'
  className?: string;
  renderAs?: 'markdown' | 'raw';
}

export const MarkdownDoc: React.FC<MarkdownDocProps> = ({
  docPath,
  className = '',
  renderAs = 'markdown',
}) => {
  const [content, setContent] = useState<string>('');
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<{ title?: string; lastUpdated?: string }>({});

  useEffect(() => {
    const fetchDoc = async () => {
      try {
        setLoading(true);
        const response = await fetch(docPath, {
          headers: {
            Accept: 'text/plain',
          },
        });
        if (!response.ok) {
          throw new Error(`Failed to load document: ${response.status}`);
        }
        const text = await response.text();

        // Strip front-matter
        const frontMatterMatch = text.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
        let cleanContent = text;
        if (frontMatterMatch) {
          const frontMatter = frontMatterMatch[1];
          cleanContent = frontMatterMatch[2];

          const frontMatterLines = frontMatter.split('\n').map(line => line.trim());
          const fmData: Record<string, string> = {};

          frontMatterLines.forEach(line => {
            const [key, ...rest] = line.split(':');
            if (key && rest.length > 0) {
              fmData[key.trim()] = rest.join(':').trim();
            }
          });

          if (fmData.title) {
            fmData.title = fmData.title.replace(/^"|"$/g, '');
          }

          setMetadata({
            title: fmData.title,
            lastUpdated: fmData.lastUpdated,
          });

          if (fmData.lastUpdated) {
            setLastUpdated(fmData.lastUpdated);
          }
        }

        setContent(cleanContent);
        setError(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load document';
        setError(errorMessage);
        Sentry.captureMessage('POLICY_LOAD_FAIL', {
          level: 'warning',
          extra: {
            docPath,
            error: errorMessage,
          },
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDoc();
  }, [docPath]);

  const renderMarkdown = (markdown: string): React.ReactElement => (
    <div className="prose prose-sm max-w-none">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
    </div>
  );

  const renderRaw = (text: string): React.ReactElement => (
    <pre className={`whitespace-pre-wrap break-words ${className}`}>{text}</pre>
  );

  if (loading) {
    return (
      <div className={className}>
        <div className="h-4 w-2/3 animate-pulse rounded bg-gray-200" />
        <div className="mt-2 h-4 w-1/2 animate-pulse rounded bg-gray-200" />
        <div className="mt-4 h-4 w-full animate-pulse rounded bg-gray-200" />
        <div className="mt-2 h-3 w-5/6 animate-pulse rounded bg-gray-200" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded border border-red-200 bg-red-50 p-4 text-red-700 ${className}`}>
        <p className="font-semibold">We couldn’t load that document.</p>
        <p className="mt-1 text-sm">
          Please refresh and try again. If it persists, contact support.
        </p>
      </div>
    );
  }

  return (
    <article className={`markdown-content ${className}`}>
      {metadata.title && (
        <h1 className="mb-6 text-3xl font-semibold text-gray-900">{metadata.title}</h1>
      )}
      {lastUpdated && <p className="mb-6 text-sm text-gray-500">Last updated {lastUpdated}</p>}
      {renderAs === 'raw' ? renderRaw(content) : renderMarkdown(content)}
    </article>
  );
};
